<script setup name="TvbVideoDetailList">
import { inject, ref, onMounted, onServerPrefetch, watch, nextTick } from 'vue'
import TvbVideoDetailItem from '../Item/index.vue'
import { getVideoRecommend } from '@/api/video'

const page = ref(1)
const limit = ref(10)
const items = ref([])
const size = inject('size')
const width = inject('width')
const defaultVideo = inject('defaultVideo')
const currentVideo = inject('currentVideo')
const playerHeight = inject('playerHeight')
const setNextVideo = inject('setNextVideo')
// 注入上一集视频设置方法，用于播放器上一集按钮功能
const setPrevVideo = inject('setPrevVideo')
const emits = defineEmits(['lm-show-list-change'])
const isFinish = ref(false)
const infiniteScrollRef = ref()
const listItemsRef = ref()
const showList = ref(false)
const lmShowList = ref(true)
const isLoading = ref(false)

const getList = async () => {
  if (isFinish.value || isLoading.value) return
  isLoading.value = true
  const res = await getVideoRecommend({
    video_id: defaultVideo.value?.id,
    category_id: defaultVideo.value?.category_id,
    exclude_ids: items.value.reduce((prev, cur) => prev.concat(cur.id), []).join(','),
    page: page.value,
    limit: limit.value
  })
  if (res.code === 0) {
    const list = res?.data?.list || []
    items.value = [...items.value, ...list]
    isFinish.value = !list?.length || list?.length < limit.value
    setNextItem()
  }
  isLoading.value = false
}

const handleLoad = () => {
  page.value++
  getList()
}

const itemsScrollTo = (top) => {
  infiniteScrollRef.value?.scrollbarInstRef?.scrollTo({ left: 0, top, behavior: 'smooth' })
}

// 计算并设置上一集和下一集视频
// 根据当前视频在播放列表中的位置，找出前一个和后一个视频
const setNextItem = () => {
  if (!items.value?.length || !currentVideo.value) return

  let nextItem = items.value[0]
  let prevItem = null

  for (let i = 0; i < items.value.length; i++) {
    const item = items.value[i]
    const _nextItem = items.value[i + 1] || {}
    const _prevItem = items.value[i - 1] || {}

    if (item.id === currentVideo.value.id) {
      // 找到当前视频，设置下一集和上一集
      if (_nextItem.id) {
        nextItem = _nextItem
      }
      if (_prevItem.id) {
        prevItem = _prevItem
      }
    }
  }

  // 设置下一集视频
  if (nextItem) {
    nextItem.hash = Math.random().toString(36).slice(2, 10)
    setNextVideo(nextItem)
  }

  // 设置上一集视频（如果存在）
  if (prevItem) {
    prevItem.hash = Math.random().toString(36).slice(2, 10)
    setPrevVideo(prevItem)
  } else {
    // 当前视频是列表第一个，没有上一集
    setPrevVideo(null)
  }
}

watch(
  lmShowList,
  (lmShowList) => {
    emits('lm-show-list-change', lmShowList)
  },
  { immediate: true }
)

const computedListHeight = () => {
  if (typeof window === 'undefined') return '0px'
  const height =
    window.innerHeight -
    (document.querySelector('.tvb-home-header')?.clientHeight || 0) -
    (document.querySelector('.tvb-video-detail_player')?.clientHeight || 0)
  return `${height > 0 ? height - 8 - 12 : 0}px`
}

const listHeight = ref('0px')

// computed(() => {
//     return width.value && ['m', 's', 'xs'].includes(size.value) ? computedListHeight() : null
// })

const show = () => {
  if (['s', 'xs'].includes(size.value)) {
    showList.value = true
  } else {
    lmShowList.value = true
  }
}

const hide = () => {
  if (['s', 'xs'].includes(size.value)) {
    showList.value = false
  } else {
    lmShowList.value = false
  }
}

onMounted(async () => {
  await getList()
  listHeight.value = computedListHeight()
})
onServerPrefetch(getList)

watch(currentVideo, setNextItem)

watch(
  [showList, width, size],
  ([showList, width, size]) => {
    if (typeof window === 'undefined') return
    listHeight.value = computedListHeight()
    const homeHeader = document.querySelector('.tvb-home-header')
    if (showList && width && homeHeader && window.scrollY <= homeHeader.clientHeight) {
      window.scroll(0, homeHeader.clientHeight)
    }
    nextTick(() => {
      document.body.style.height = ['m', 's', 'xs'].includes(size) && showList ? '100vh' : ''
      document.body.style.width = ['m', 's', 'xs'].includes(size) && showList ? '100vw' : ''
      document.body.style.overflow = ['m', 's', 'xs'].includes(size) && showList ? 'hidden' : ''
      const containerContent = document.querySelector('.tvb-video-detail_container-content')
      if (containerContent) {
        containerContent.style.boxShadow =
          ['m', 's', 'xs'].includes(size) && showList ? '0 0 100px 1000px #FFF' : ''
      }
      setTimeout(() => {
        document.body.style.overflow = ['m', 's', 'xs'].includes(size) && showList ? 'hidden' : ''
      }, 100)
    })
  },
  {
    deep: true,
    immediate: true
  }
)

defineExpose({
  show,
  hide
})
</script>

<template>
  <div
    class="tvb-video-detail_list"
    :class="{ [size]: size, show: ['s', 'xs'].includes(size) ? showList : lmShowList }"
    :style="{
      '--tvb-video-detail-list-height': listHeight,
      '--tvb-video-detail-list-player-height': playerHeight
    }"
  >
    <div class="tvb-video-detail_list-header">
      <div class="tvb-video-detail_list-header-container">
        <h4 class="tvb-video-detail_list-title">播放列表</h4>
        <tvb-svg-icon name="close" class="tvb-video-detail_list-close" @click="hide" />
      </div>
    </div>
    <div class="tvb-video-detail_list-items" ref="listItemsRef">
      <n-infinite-scroll
        style="height: 100%"
        :distance="10"
        @load="handleLoad"
        ref="infiniteScrollRef"
      >
        <tvb-video-detail-item
          v-for="item in items"
          :key="item.id"
          :info="item"
          @scroll-to="itemsScrollTo"
        />
      </n-infinite-scroll>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-video-detail_list {
  box-sizing: border-box;
  // 容器圆角按设计稿调整为 20px
  border-radius: 20px;
  border: 0.5px solid rgba(163, 178, 188, 0.35);
  background: #fcfcfc;
  overflow: hidden;
  width: 400px;
  min-width: 400px;
  display: flex;
  flex-direction: column;
  // 使用CSS变量高度，同时设置最小高度确保内容可见
  height: var(--tvb-video-detail-list-player-height);
  min-height: 360px;

  @media (max-width: 1440px) {
    &.xl {
      // aspect-ratio: 0.7590132827324478 / 1;
    }
  }

  &.l,
  &.m {
    @media (max-width: 1143px) {
      width: 100%;
      height: 0px;
      border: 0.5px solid rgba(163, 178, 188, 0);
      margin-top: 0px;
      transition:
        height 0.15s ease-in,
        border 0.15s ease-in,
        opacity 0.15s ease-in;
      opacity: 0;
      min-height: 0;

      &.show {
        min-height: 360px;
        margin-top: 16px;
        border: 0.5px solid rgba(163, 178, 188, 0.35);
        height: 500px;
        opacity: 1;
        // 展开悬浮态增加投影（工单：屏宽 768 以上展开的播放列表缺阴影）
        box-shadow: 0 4px 14px -4px rgba(0, 0, 0, 0.1);
      }

      .tvb-video-detail_list-header {
        .tvb-video-detail_list-header-container {
          .tvb-video-detail_list-close {
            display: block;
          }
        }
      }
    }
  }

  &.s,
  &.xs {
    width: 100%;
    aspect-ratio: unset;
    height: var(--tvb-video-detail-list-height);
    min-height: unset;
    position: fixed;
    left: 0;
    bottom: -100vh;
    // 弹层提升至播放器 sticky 层（z-index: 9）之上，避免遮挡"穿模"
    z-index: 10;
    transition: bottom 0.15s ease-in;
    min-width: unset;
    background: #f5f6f7;
    border-radius: 20px 20px 0 0;
    border: 0;

    &.show {
      bottom: 0;
    }

    .tvb-video-detail_list-header {
      padding: 14px 16px 0;

      .tvb-video-detail_list-header-container {
        padding-bottom: 16px;
        border-bottom: 0.5px solid rgba(163, 178, 188, 0);

        .tvb-video-detail_list-title {
          font-size: 16px;
          font-weight: 500;
          line-height: 24px;
          letter-spacing: 0.5px;
        }

        .tvb-video-detail_list-close {
          display: block;
        }
      }
    }

    .tvb-video-detail_list-items {
      border-radius: 20px 20px 0 0;
      background: #fff;
      overflow: hidden;
    }
  }

  &.l {
    aspect-ratio: 0.7590132827324478 / 1;
  }

  .tvb-video-detail_list-header {
    padding: 14px 16px 0;
    background: inherit;

    .tvb-video-detail_list-header-container {
      padding-bottom: 16px;
      border-bottom: 0.5px solid rgba(163, 178, 188, 0.35);
      display: flex;
      justify-content: space-between;
      align-items: center;

      .tvb-video-detail_list-close {
        width: 24px;
        height: 24px;
        cursor: pointer;
        display: none;
      }

      .tvb-video-detail_list-title {
        // 标题颜色按设计稿调整
        color: #1c191a;
        font-size: 17px;
        font-weight: 500;
        line-height: 26px;
        letter-spacing: 0.5px;
      }
    }
  }

  .tvb-video-detail_list-items {
    flex: 1;
    // padding: 0 12px;
    height: calc(100% - 55px);
  }
}
</style>
